import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import NewSideBar from "../../components/Navigation/NewSideBar";
import { postInterview } from "../../services/Interviews/interviewService";
import { LayoutContext } from "../../utils/contexts/LayoutContext";
import TextField from "../../components/forms/TextField/TextField";
import { getInterviewTypes } from "../../services/Interviews/inteviewTypesService";
import Select from "../../components/forms/Select/Select";
import { getInterviewSkillOptions, getInterviewTemplateOptions } from "../../services/Interviews/interviewSkillService";

export default function CreateInterview({ embedded = false }) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange" });
  const navigate = useNavigate();
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setPadding } = useContext(LayoutContext);

  useEffect(() => {
    if (embedded) return undefined;
    setPadding("p-0");
    return () => setPadding("p-4");
  }, [embedded, setPadding]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    Promise.all([
      getInterviewTypes({ signal: controller.signal }),
      getInterviewTemplateOptions({ signal: controller.signal }),
      getInterviewSkillOptions({ signal: controller.signal }),
    ])
      .then(([typeData, templateData, skillData]) => {
        setInterviewTypes(typeData.results || typeData || []);
        setTemplates(templateData);
        setSkills(skillData);
      })
      .catch((err) => {
        console.error("Get Interview Types Failed", err);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const selectedSkills = watch("skills", []);
  const toggleSkill = (uuid) => setValue(
    "skills",
    selectedSkills.includes(uuid)
      ? selectedSkills.filter((value) => value !== uuid)
      : [...selectedSkills, uuid],
    { shouldDirty: true, shouldValidate: true },
  );

  const onSubmit = async (data) => {
    postInterview(data)
      .then((res) => {
        console.log("Post Success");
        console.log(res);
        navigate(`/organization/interviews/${res.uuid}/overview`);
      })
      .catch((err) => {
        console.error("Post Failed", err);
      });
  };

  return (
    <div className="flex h-full">
      {!embedded && <NewSideBar />}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col w-full overflow-auto"
      >
        <div className={`mx-auto w-full max-w-3xl flex-1 ${embedded ? 'py-4' : 'p-8'}`}>
          <div className="mb-8">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">Interview setup</p>
            <h1 className="mt-2 text-3xl font-bold text-gray-950 dark:text-white">Create an interview</h1>
            <p className="mt-2 text-gray-500">Start with the purpose and type. Questions, sessions, and evaluation status will live in one workspace.</p>
          </div>
          <TextField
            {...register("name", { required: "Interview title is required" })}
            label="Interview Title"
            placeholder="Interview Title"
            error={errors.name}
          />
          <p className="text-gray-500 mb-6 text-sm">
            Give your Interview a Title
          </p>
          <Controller
            name="type"
            control={control}
            rules={{ required: "Interview type is required" }}
            render={({ field }) => (
              <Select
                disabled={loading}
                loading={loading}
                className="mb-6"
                options={interviewTypes.map((type) => ({
                  label: type.name,
                  value: type.uuid,
                }))}
                value={field.value}
                onChange={(v) => field.onChange(v.value)}
                errorMsg={errors.type?.message}
                label="Interview Type"
                placeholder="Select Interview Type"
              />
            )}
          />

          <section className="mb-7 rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Skill framework</h2>
            <p className="mt-1 text-sm text-slate-500">Start from a reusable template, then adjust the copied skills for this interview. Future template changes will not alter this interview.</p>
            <Controller name="template" control={control} defaultValue="" render={({ field }) => <label className="mt-4 block text-sm font-semibold">Skill template<select className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 dark:border-slate-600 dark:bg-slate-900" value={field.value || ''} onChange={(event) => { const value = event.target.value; field.onChange(value); const template = templates.find((item) => item.uuid === value); if (template) setValue('skills', template.skill || [], { shouldDirty: true, shouldValidate: true }); }}><option value="">Build manually</option>{templates.filter((item) => item.is_active !== false).map((template) => <option key={template.uuid} value={template.uuid}>{template.name} ({template.skill?.length || 0} skills)</option>)}</select></label>} />
            <Controller name="skills" control={control} defaultValue={[]} rules={{ validate: (value) => value?.length > 0 || 'Select at least one interview skill' }} render={() => <fieldset className="mt-4"><legend className="text-sm font-semibold">Interview skills</legend><div className="mt-2 grid max-h-64 gap-2 overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-2 dark:border-slate-600 dark:bg-slate-900">{skills.map((skill) => <label key={skill.uuid} className="flex cursor-pointer items-start gap-2 rounded-lg p-2 hover:bg-slate-50 dark:hover:bg-slate-800"><input type="checkbox" className="mt-1" checked={selectedSkills.includes(skill.uuid)} onChange={() => toggleSkill(skill.uuid)} /><span><strong className="block text-sm">{skill.name}</strong><small className="text-slate-500">{skill.type_name || 'Managed skill'}</small></span></label>)}</div>{errors.skills && <span className="mt-1 block text-xs text-red-600">{errors.skills.message}</span>}</fieldset>} />
          </section>

          <div className="flex flex-col mb-6">
            <textarea
              {...register("description", {
                required: "Description is required",
              })}
              rows={4}
              placeholder="Description..."
              className="
              dark:bg-gray-800 dark:text-white dark:border-gray-600
              w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              label="Interview Description"
              error={errors.description}
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1">
                {errors.description.message}
              </p>
            )}
          </div>
          <p className="text-gray-500 mb-6 text-sm">
            Give a brief description of the interview’s purpose, context, and
            goals.
          </p>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              className="px-4 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
              onClick={() => navigate("/organization/interviews")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Create Interview
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
